/**
 * Vowed iPhone Gate PWA — app logic (D-046, Leg 11).
 *
 * The operator "Add to Home Screen"s this once and enrolls it. From then on it is the
 * channel-separated gate: it receives grant requests via the relay, shows the descriptor +
 * the compare-and-confirm code, gates approval behind Face ID (WebAuthn), and returns an
 * Ed25519-signed verdict the daemon accepts. The gate private key is a non-extractable
 * WebCrypto key stored in IndexedDB — it never leaves the device.
 *
 * Delivery: the daemon POSTs a request to the relay; this PWA long-polls the relay for it
 * and POSTs the signed verdict back; the relay returns it to the daemon's held request.
 */

import {
  generateGateKeypair,
  signVerdict,
  signEnrollNonce,
  computeSafetyNumber,
  base64ToBytes,
  enrollAttestationHash,
  ENROLL_ATTEST_OP,
  MINT_SERVICE_TOKEN_OP,
  mintDescriptorHash,
  type MintDescriptorFields,
  type SignedVerdictFields,
  type GateKeypair,
} from "./gate-sign.js";
import { syncPush, type PushEnv, type PushPermission, type PushState, type SyncPushResult } from "./push.js";
import {
  approvalChallenge,
  enrollChallenge,
  assertFaceId,
  setUpFaceId,
  type FaceIdFailure,
  type FaceIdResult,
  type SetupResult,
  type WebAuthnEnv,
} from "./faceid.js";

export interface StoredGate {
  privateKey: CryptoKey; // non-extractable
  pubkeyB64: string;
  gate_id: string;
}

const DB = "vowed-gate";
const STORE = "key";

function idb(): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function dbGet<T>(key: string): Promise<T | undefined> {
  const db = await idb();
  return new Promise((res, rej) => {
    const r = db.transaction(STORE).objectStore(STORE).get(key);
    r.onsuccess = () => res(r.result as T);
    r.onerror = () => rej(r.error);
  });
}
async function dbSet(key: string, val: unknown): Promise<void> {
  const db = await idb();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

/** Fleet default relay — pre-fills a fresh install so the operator needn't paste it. */
export const DEFAULT_RELAY = "https://vowed-relay.chris-270.workers.dev";
function relayUrl(): string {
  return localStorage.getItem("vowed-relay-url") || DEFAULT_RELAY;
}

/** localStorage key for the per-vault blind index (`?v=`) captured from the enrollment payload. */
export const VAULT_ID_KEY = "vowed-gate-vault-id";

/** The per-vault blind index this PWA targets, or null (legacy) if it never received one. */
export function vaultId(): string | null {
  return localStorage.getItem(VAULT_ID_KEY);
}

/**
 * D-069 Tier-1: decorate a /gate/* path with the mandatory unguessable `?v=` blind index so the
 * relay routes it to the correct vault gate. No-op (legacy path) when no vault-id is known — the
 * relay tolerates the missing id only while its GATE_LEGACY_VAULT_OK rollout valve is set.
 * Handles paths that already carry a query string (e.g. /gate/verdict?handle=…).
 */
export function withVault(path: string): string {
  const v = vaultId();
  if (!v) return path;
  return path + (path.includes("?") ? "&" : "?") + "v=" + encodeURIComponent(v);
}

/**
 * Capture the enrollment payload params (`v` = vault-id, `r` = relay) from THIS PWA's own URL and
 * persist them, so every subsequent /gate/* call targets the right vault gate. Called at startup.
 * Idempotent + defensive: only accepts a well-formed hex blind-index (never downgrades a stored id
 * to a malformed one). Returns true if a vault-id was captured.
 */
export function captureEnrollmentParams(search: string = (typeof location !== "undefined" ? location.search : "")): boolean {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return false;
  }
  const r = params.get("r");
  if (r) localStorage.setItem("vowed-relay-url", r.replace(/\/$/, ""));
  const v = params.get("v");
  if (v && /^[0-9a-fA-F]{32,128}$/.test(v)) {
    localStorage.setItem(VAULT_ID_KEY, v);
    return true;
  }
  return false;
}

/**
 * Accept a pasted enrollment link (or its query string, or a bare hex vault-id) and persist the
 * vault-id + relay through the same validated capture path the enrollment URL uses.
 *
 * WHY THIS EXISTS (2026-07-27 live-smoke finding): an INSTALLED Home-Screen app launches at the
 * manifest start_url with no query and iOS gives it storage isolated from Safari — so the
 * now-mandatory `?v=` vault-id (D-069 Tier-1) can never reach it via the enrollment URL. Every
 * installed app (including a customer's replacement phone) needs an in-app way to receive the
 * enrollment parameters; this is it.
 */
export function applyEnrollmentInput(input: string): boolean {
  const t = input.trim();
  if (!t) return false;
  // A bare hex blind-index, pasted directly.
  if (/^[0-9a-fA-F]{32,128}$/.test(t)) return captureEnrollmentParams("?v=" + t);
  // A full enrollment URL.
  try {
    const u = new URL(t);
    return captureEnrollmentParams(u.search);
  } catch {
    /* not a URL — fall through to query-string form */
  }
  // A bare query string ("ev=1&r=…&v=…", with or without the leading "?").
  return captureEnrollmentParams(t.startsWith("?") ? t : "?" + t);
}

/** VAPID public key (applicationServerKey) — pairs with the relay's VAPID_PRIVATE_KEY secret. */
const VAPID_PUBLIC_KEY = "BICQafdxixWyEpbwjNPnfREqdJr46x6ff_hZB3pX9SzaoxJI2u23CXKjrEc3RfVFwMNURTyfFRMm-Qh6U0sChCY";
function urlB64ToBytes(b64: string): Uint8Array {
  const base64 = (b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Are the push APIs (Notification + serviceWorker + PushManager) available here? */
function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

/**
 * Per-install record of the push endpoint we last registered with the relay. Stored in
 * localStorage (scoped to this installed PWA) so we can detect when the OS has rotated/dropped
 * the subscription and re-register only the delta, rather than re-POSTing on every open.
 */
const PUSH_ENDPOINT_KEY = "vowed-push-endpoint";
function persistEndpoint(endpoint: string): void {
  try {
    localStorage.setItem(PUSH_ENDPOINT_KEY, endpoint);
  } catch {
    /* private mode / storage disabled — re-register next open */
  }
}
function storedEndpoint(): string | null {
  try {
    return localStorage.getItem(PUSH_ENDPOINT_KEY);
  } catch {
    return null;
  }
}

/** POST a live subscription to the relay's per-install registry. Returns true on 2xx. */
async function postSubscription(sub: PushSubscription): Promise<boolean> {
  const res = await fetch(`${relayUrl()}${withVault("/gate/subscribe")}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(sub),
  });
  return res.ok;
}

/**
 * Turn on Web Push so a request wakes this phone even when the app is backgrounded.
 * Requires the INSTALLED Home-Screen app (iOS 16.4+) and a user gesture for the permission
 * prompt. Best-effort: returns false (no throw) if unsupported / denied. The push is a
 * payload-less doorbell — it carries no secrets; the app fetches the request when opened.
 *
 * On success the endpoint is persisted per-install so the open-time self-heal ({@link
 * syncPushSubscription}) can tell a still-valid subscription from a rotated/lost one.
 */
export async function enablePush(): Promise<boolean> {
  try {
    if (!pushSupported()) return false;
    if ((await Notification.requestPermission()) !== "granted") return false;
    const reg = await navigator.serviceWorker.ready;
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToBytes(VAPID_PUBLIC_KEY) }));
    const ok = await postSubscription(sub);
    if (ok) persistEndpoint(sub.endpoint);
    return ok;
  } catch {
    return false;
  }
}

/** Read this install's live push state for the open-time reconcile (see push.ts `pushDecision`). */
async function browserPushState(): Promise<PushState> {
  if (!pushSupported()) {
    return { supported: false, permission: "default", currentEndpoint: null, registeredEndpoint: storedEndpoint() };
  }
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return {
    supported: true,
    permission: Notification.permission as PushPermission,
    currentEndpoint: sub?.endpoint ?? null,
    registeredEndpoint: storedEndpoint(),
  };
}

/** The browser implementation of the side effects `syncPush` orchestrates. */
function browserPushEnv(onPrompt: (a: "prompt-reenable" | "request-permission") => void): PushEnv {
  return {
    getState: browserPushState,
    async subscribe() {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToBytes(VAPID_PUBLIC_KEY),
        });
        return sub.endpoint;
      } catch {
        return null; // permission may have been revoked between read and subscribe
      }
    },
    async register() {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        return sub ? await postSubscription(sub) : false;
      } catch {
        return false;
      }
    },
    persist: persistEndpoint,
    onPrompt,
  };
}

/**
 * Reconcile + self-heal this install's push subscription on app open: silently re-subscribe if
 * the OS dropped it (Focus Mode / restart / expiry), re-register if it rotated, or surface a
 * re-enable prompt if permission was revoked. Best-effort and gesture-free on the silent paths
 * (permission already granted). Returns the action taken AND whether it actually succeeded so
 * the UI reflects the true wake-channel state (never "on" for a failed heal).
 */
export async function syncPushSubscription(
  onPrompt: (a: "prompt-reenable" | "request-permission") => void = () => {},
): Promise<SyncPushResult> {
  return syncPush(browserPushEnv(onPrompt));
}
async function relay(path: string, body?: unknown): Promise<any> {
  const res = await fetch(relayUrl() + withVault(path), {
    method: body ? "POST" : "GET",
    headers: body ? { "content-type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`relay ${path} → ${res.status}`);
  return res.status === 204 ? null : res.json();
}

const WEBAUTHN_CRED_KEY = "webauthn-cred-id";

/**
 * The browser WebAuthn surface for faceid.ts. Face ID needs Safari or the installed Home-Screen
 * app; where WebAuthn is unavailable every approval fails closed (there is no manual fallback).
 */
function browserWebAuthnEnv(): WebAuthnEnv {
  return {
    rpId: location.hostname,
    loadCredentialId: () => dbGet<ArrayBuffer>(WEBAUTHN_CRED_KEY),
    saveCredentialId: (id) => dbSet(WEBAUTHN_CRED_KEY, id),
    get: (o) => navigator.credentials.get(o),
    create: (o) => navigator.credentials.create(o),
  };
}

/** True when this phone has a Face ID passkey that approvals are pinned to. */
export async function hasFaceId(): Promise<boolean> {
  return !!(await dbGet<ArrayBuffer>(WEBAUTHN_CRED_KEY));
}

/**
 * The explicit "Set up Face ID" action (J-3529): create this phone's Face ID passkey, replacing
 * the pinned one only if creation succeeds. Nothing else in the app ever creates a passkey.
 */
export async function setUpFaceIdOnThisPhone(): Promise<SetupResult> {
  return setUpFaceId(browserWebAuthnEnv());
}

/** Poll a relay GET endpoint until it returns a body (not 204) or the deadline passes. */
async function pollRelay(path: string, timeoutMs: number): Promise<any | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const v = await relay(path).catch(() => null);
    if (v) return v;
    await new Promise((r) => setTimeout(r, 1500));
  }
  return null;
}

/**
 * One-time enrollment (rendezvous): POST our pubkey → wait for the Mac to begin →
 * independently compute + confirm the safety number → Face ID → sign the nonce → POST proof.
 * On the Mac, the operator runs `vowed gate enroll` concurrently.
 */
/**
 * Resolve the gate's signing keypair for enrollment: REUSE the already-persisted key if one
 * exists, else generate a fresh one (idempotent enroll). Re-enrolling then advertises the SAME
 * pubkey instead of minting a NEW gate identity every time — the churn that produced multiple
 * dead gate_ids for one phone, none matching the key later used to sign unlocks (J-0302 #2). A
 * new key is minted ONLY when none is persisted (first enroll, or after iOS evicted the store).
 * Deps are injected so the reuse decision is unit-testable without IndexedDB / WebCrypto.
 */
export async function resolveGateKeypair(
  loadStored: () => Promise<StoredGate | undefined>,
  gen: () => Promise<GateKeypair>,
): Promise<{ privateKey: CryptoKey; pubkeyB64: string; reused: boolean }> {
  const existing = await loadStored();
  if (existing?.privateKey && existing.pubkeyB64) {
    return { privateKey: existing.privateKey, pubkeyB64: existing.pubkeyB64, reused: true };
  }
  const kp = await gen();
  return { privateKey: kp.privateKey, pubkeyB64: kp.pubkeyB64, reused: false };
}

export async function enroll(friendlyName: string, onSafetyNumber: (n: string) => Promise<boolean>): Promise<void> {
  // Best-effort: ask the browser to keep our storage, reducing iOS's ~7-day eviction of the
  // gate key (the churn source). Not a guarantee — the durable-across-eviction fix is the
  // WebAuthn-PRF seed redesign, tracked separately. Never throws.
  try { await navigator.storage?.persist?.(); } catch { /* best-effort */ }

  // J-3529: Face ID is set up by its own explicit step, never created as a side effect. Check
  // before contacting the Mac so the operator is not left waiting on a step that must fail.
  if (!(await hasFaceId())) throw new Error("Set up Face ID on this phone first, then enroll");

  // Idempotent: reuse the persisted key if we still have it, else mint one.
  const { privateKey, pubkeyB64 } = await resolveGateKeypair(() => dbGet<StoredGate>("gate"), generateGateKeypair);
  await relay("/gate/enroll/begin", { pubkey: pubkeyB64, friendly_name: friendlyName });

  const resp = await pollRelay("/gate/enroll/response", 120_000);
  if (!resp) throw new Error("Timed out — run `vowed gate enroll` on your Mac, then retry");

  const safety = await computeSafetyNumber(base64ToBytes(pubkeyB64), base64ToBytes(resp.daemon_pub));
  if (!(await onSafetyNumber(safety))) throw new Error("Safety number not confirmed — aborted");
  const uv = await assertFaceId(await enrollChallenge(resp.gate_id, pubkeyB64, resp.nonce), browserWebAuthnEnv());
  if (!uv.ok) throw new Error(`Not enrolled — ${notApprovedReason(uv.reason)}`);

  const proof = await signEnrollNonce(resp.nonce as string, privateKey);
  await relay("/gate/enroll/proof", { pubkey: pubkeyB64, proof, gate_id: resp.gate_id });
  await dbSet("gate", { privateKey, pubkeyB64, gate_id: resp.gate_id } as StoredGate);
}

/**
 * A pending gate request as delivered via the relay. It covers BOTH the legacy credential
 * GRANT and the phone-gated UNLOCK. The `descriptor.op` field is the discriminator:
 *   - op === "unlock"  → an UNLOCK request (authorizes a daemon state transition; no scope)
 *   - op absent        → a credential GRANT (carries wallets/ops/names scope)
 * (Per the daemon's buildUnlockDescriptor, the unlock descriptor carries op="unlock", empty
 * scope arrays, and reason "unlock".)
 */
export interface PendingGrant {
  request_id: string;
  code: string;
  descriptor: {
    wallets: string[];
    ops: string[];
    names: string[];
    reason: string;
    window: unknown;
    /** SPEC A2.7 — "unlock" for an unlock request; "enroll_attest" for a new-device attestation;
     *  absent for a credential grant. */
    op?: string;
    // R1 companion (D-073) — present ONLY when op === "enroll_attest" (SUBSEQUENT enrollment).
    gate_id?: string;       // the new gate's id
    new_pubkey?: string;    // the new gate's base64 pubkey — the phone fingerprints + hashes THIS
    nonce?: string;         // the enrollment nonce
    friendly_name?: string; // operator-set display name of the new device
    fingerprint?: string;   // display convenience; the phone recomputes to verify
    // D-028a — present ONLY when op === "mint_service_token" (the frozen, daemon-built descriptor).
    requestor_session?: string;
    tags?: string[];
    requested_at?: number;
    service_id?: string;
    machine?: string;
    target?: { daemon_id: string; vault_id: string };
  };
  target: { daemon_id: string; vault_id: string; machine?: string };
  /** The hash the gate must sign — ABSENT for a service-token mint (the phone recomputes it). */
  approved_descriptor_hash?: string;
  /** Legacy/test field name. The daemon actually posts `expires_at` (SEC-CB-015). */
  expiry?: number;
  /** Request expiry (epoch seconds) as the daemon posts it. */
  expires_at?: number;
}

/** True when this pending request is a STANDING SERVICE-TOKEN MINT (admin op, D-028a). */
export function isMintRequest(req: PendingGrant): boolean {
  return req.descriptor?.op === MINT_SERVICE_TOKEN_OP;
}

/** The plain-language warning every service-token card carries. */
export const STANDING_NOTE = "Standing credential — survives relock";

/**
 * Everything the service-token card shows, plus the exact descriptor rebuilt from those shown
 * values (which is what gets hashed and signed). Pure — no DOM, no IndexedDB.
 */
export interface MintCard {
  wallet: string;
  names: string[];
  ops: string[];
  serviceId: string;
  ttlSeconds: number;
  lifetimeDays: number;
  /** Token expiry = requested_at + ttl (epoch ms) — exactly what the daemon will mint. */
  expiresAtMs: number;
  /** YYYY-MM-DD (UTC) of the token expiry. */
  expiryDate: string;
  machine: string;
  reason: string;
  code: string;
  requestorSession: string;
  standing: string;
  fields: MintDescriptorFields;
}

function isStrArray(x: unknown): x is string[] {
  return Array.isArray(x) && x.every((s) => typeof s === "string");
}

/**
 * Build the service-token card from a pending request, REFUSING anything malformed (so the phone
 * never signs a request it cannot fully display). The hashed descriptor is rebuilt from the shown
 * values only — an unexpected extra field from the relay is dropped, which changes the hash, which
 * the daemon rejects.
 */
export function mintCard(req: PendingGrant): MintCard {
  const d = req.descriptor;
  const bad = (why: string): never => {
    throw new Error(`Malformed service-token request (${why}) — deny it`);
  };
  if (!d || d.op !== MINT_SERVICE_TOKEN_OP) bad("op");
  if (!isStrArray(d.wallets) || d.wallets.length !== 1) bad("wallet");
  if (!isStrArray(d.names) || d.names.length === 0) bad("secret names");
  if (!isStrArray(d.ops) || d.ops.length === 0) bad("ops");
  if (!Array.isArray(d.tags) || d.tags.length !== 0) bad("tags");
  const w = d.window as { ttl_s?: unknown } | undefined;
  const ttl = w && typeof w === "object" ? w.ttl_s : undefined;
  if (typeof ttl !== "number" || !Number.isInteger(ttl) || ttl <= 0) bad("lifetime");
  if (typeof d.requested_at !== "number" || !Number.isInteger(d.requested_at)) bad("requested_at");
  for (const [k, v] of [
    ["service id", d.service_id],
    ["machine", d.machine],
    ["reason", d.reason],
    ["requestor", d.requestor_session],
  ] as const) {
    if (typeof v !== "string" || !v) bad(k);
  }
  const t = d.target;
  if (!t || typeof t.daemon_id !== "string" || typeof t.vault_id !== "string") bad("target");

  const ttlSeconds = ttl as number;
  const expiresAtMs = ((d.requested_at as number) + ttlSeconds) * 1000;
  const fields: MintDescriptorFields = {
    op: MINT_SERVICE_TOKEN_OP,
    requestor_session: d.requestor_session as string,
    wallets: [d.wallets[0]],
    ops: [...d.ops],
    names: [...d.names],
    tags: [],
    window: { ttl_s: ttlSeconds },
    requested_at: d.requested_at as number,
    service_id: d.service_id as string,
    reason: d.reason,
    machine: d.machine as string,
    target: { daemon_id: t!.daemon_id, vault_id: t!.vault_id },
  };
  return {
    wallet: d.wallets[0],
    names: [...d.names],
    ops: [...d.ops],
    serviceId: fields.service_id,
    ttlSeconds,
    lifetimeDays: Math.round(ttlSeconds / 86_400),
    expiresAtMs,
    expiryDate: new Date(expiresAtMs).toISOString().slice(0, 10),
    machine: fields.machine,
    reason: fields.reason,
    code: req.code,
    requestorSession: fields.requestor_session,
    standing: STANDING_NOTE,
    fields,
  };
}

/** A plain-words summary of a mint: every security-relevant field the card shows. */
export function mintPrompt(c: MintCard): string {
  return [
    `Mint a STANDING service token for "${c.serviceId}"?`,
    `Wallet: ${c.wallet}`,
    `Secrets: ${c.names.join(", ")}`,
    `Ops: ${c.ops.join(", ")}`,
    `Expires: ${c.expiryDate} (${c.lifetimeDays} days)`,
    `Machine: ${c.machine}`,
    `Reason: ${c.reason}`,
    `Code: ${c.code}`,
    `${c.standing}.`,
  ].join("\n");
}

/** True when this pending request is an UNLOCK (vs a credential grant) — by the op discriminator. */
export function isUnlockRequest(req: PendingGrant): boolean {
  return req.descriptor?.op === "unlock";
}

/** True when this pending request is a NEW-DEVICE ENROLLMENT ATTESTATION (R1 companion, D-073). */
export function isEnrollAttestRequest(req: PendingGrant): boolean {
  return req.descriptor?.op === ENROLL_ATTEST_OP;
}

/** Plain words for why Face ID did not pass. Every case means nothing was signed. */
export function notApprovedReason(reason: FaceIdFailure): string {
  switch (reason) {
    case "no-passkey":
      return "Face ID is not set up on this phone. Tap Set up Face ID first.";
    case "cancelled":
      return "Face ID was cancelled. Nothing was signed.";
    default:
      return "Face ID did not verify. Nothing was signed.";
  }
}

/** What `decide` did: sent a signed verdict, or (Face ID did not pass) sent and signed nothing. */
export type DecisionOutcome =
  | { sent: true; decision: "approve" | "deny" }
  | { sent: false; reason: FaceIdFailure };

/** The side effects `decide` performs, injected so the fail-closed contract is unit-testable. */
export interface DecideDeps {
  loadGate(): Promise<StoredGate | undefined>;
  /** Face ID over a challenge bound to the verdict; see faceid.ts `assertFaceId`. */
  faceId(challenge: Uint8Array): Promise<FaceIdResult>;
  sign(fields: SignedVerdictFields, privateKey: CryptoKey, pubkeyB64: string): Promise<SignedVerdictFields & { sig: string; signer_pubkey: string }>;
  send(verdict: SignedVerdictFields & { sig: string; signer_pubkey: string }): Promise<void>;
  record(entry: HistoryEntry): Promise<void>;
  /** 16 random bytes, hex — the verdict nonce. */
  nonce(): string;
}

function browserDecideDeps(): DecideDeps {
  return {
    loadGate: () => dbGet<StoredGate>("gate"),
    faceId: (challenge) => assertFaceId(challenge, browserWebAuthnEnv()),
    sign: signVerdict,
    send: async (verdict) => {
      await relay("/gate/verdict", verdict);
    },
    record: recordHistory,
    nonce: () => bytesToHex(crypto.getRandomValues(new Uint8Array(16))),
  };
}

/**
 * Approve (or deny) a pending request: build the verdict → (approve only) Face ID over a challenge
 * derived from that exact verdict → sign it → send it. Handles every request kind. For an unlock
 * the signed verdict carries op="unlock" so the daemon's verifyUnlock accepts it (SEC-PGU-001).
 *
 * J-3529 / SEC-CB-020: an approval FAILS CLOSED. If Face ID is not set up, is cancelled, times out,
 * errors or returns anything but a user-verified assertion over this verdict, nothing is signed,
 * nothing is sent and nothing is recorded; the request stays pending until the operator retries,
 * denies, or it expires. A deny needs no Face ID: refusing is always safe.
 */
export async function decide(
  req: PendingGrant,
  approve: boolean,
  deps: DecideDeps = browserDecideDeps(),
): Promise<DecisionOutcome> {
  const gate = await deps.loadGate();
  if (!gate) throw new Error("Not enrolled");
  const enrollAttest = isEnrollAttestRequest(req);
  const mint = isMintRequest(req);

  // SIGN-WHAT-YOU-SEE (R1 companion, D-073): for a new-device attestation the phone RECOMPUTES the
  // descriptor hash AND the fingerprint from the request's {gate_id, new_pubkey, nonce} — it never
  // signs a relay-supplied hash. So a compromised relay/CLI that substituted a rogue new_pubkey
  // would produce a hash over a DIFFERENT key than the fingerprint the operator visually confirmed,
  // and gate_enroll_complete would reject it — the operator can never be tricked into attesting a
  // key they did not see.
  let approvedHash = req.approved_descriptor_hash;
  let verdictTarget = req.target;
  if (mint) {
    // D-028a: the daemon sends NO hash for a service-token mint. Recompute it from exactly the
    // fields shown on the card. mintCard throws on anything it cannot fully display: an APPROVAL is
    // then impossible, but a DENY still goes out (the daemon burns a deny before any hash check,
    // and it counts toward the 2-denial cooldown).
    let card: MintCard | null = null;
    try {
      card = mintCard(req);
    } catch (e) {
      if (approve) throw e;
    }
    if (card) {
      approvedHash = await mintDescriptorHash(card.fields);
      verdictTarget = card.fields.target;
    }
  } else if (enrollAttest) {
    const gateId = req.descriptor.gate_id ?? "";
    const newPubkey = req.descriptor.new_pubkey ?? "";
    const nonce = req.descriptor.nonce ?? "";
    if (!gateId || !newPubkey || !nonce) throw new Error("Malformed enrollment-attestation request");
    approvedHash = await enrollAttestationHash(gateId, newPubkey, nonce);
  }

  // Frozen: the challenge below is computed from these exact fields, and these exact fields are
  // what get signed. Nothing may change between the Face ID assertion and the signature.
  const fields: SignedVerdictFields = Object.freeze({
    request_id: req.request_id,
    decision: approve ? "approve" : "deny",
    approved_descriptor_hash: approvedHash as string,
    code: req.code,
    nonce: deps.nonce(),
    gate_device_id: gate.gate_id,
    target: verdictTarget,
    // SEC-CB-015: the daemon posts `expires_at`; older fixtures used `expiry`. Sign whichever is
    // present so the daemon's verdict-expiry check has a value (the mint path REQUIRES one).
    expiry: (req.expiry ?? req.expires_at) as number,
    // Mirror the daemon: fold op ONLY when present (unlock / enroll_attest). A grant omits op, so
    // its signed bytes are byte-identical to before — grant verdicts keep verifying unchanged.
    ...(req.descriptor?.op !== undefined ? { op: req.descriptor.op } : {}),
  } satisfies SignedVerdictFields);

  if (approve) {
    const uv = await deps.faceId(await approvalChallenge(fields));
    if (!uv.ok) return { sent: false, reason: uv.reason };
  }

  const verdict = await deps.sign(fields, gate.privateKey, gate.pubkeyB64);
  await deps.send(verdict);
  // Record locally after the verdict is sent (best-effort — never block/fail the decision on it).
  await deps.record(toHistoryEntry(req, approve)).catch(() => {});
  return { sent: true, decision: fields.decision };
}

function bytesToHex(b: Uint8Array): string {
  return [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

export async function isEnrolled(): Promise<boolean> {
  return !!(await dbGet<StoredGate>("gate"));
}

/* ─────────────────────────── Request history (broker-V2 §2) ───────────────────────────
 * A LOCAL, read-only log of requests this gate has decided. Persisted in the SAME IndexedDB
 * key-value store (key "history") — no new object store, no DB version bump, no new network
 * surface. Every field is sourced from the request descriptor the gate already received +
 * the verdict it already sent; nothing new is recorded. The gate only knows the outcomes it
 * acts on (approved/denied) — it has no signal for a request that timed out without it acting
 * (the relay simply stops returning it), so "expired" is intentionally NOT recorded here.
 */

const HISTORY_KEY = "history";
/** Bound the log so a long-lived install can't grow IndexedDB without limit. */
export const HISTORY_MAX = 100;

export interface HistoryEntry {
  request_id: string;
  ts: number; // epoch ms — when this gate decided the request
  kind: "unlock" | "grant" | "enroll" | "mint";
  machine: string; // target.machine ?? target.daemon_id
  scope: string; // "wallets · ops" for a grant; "Unlock the vault" for an unlock; new-device for enroll
  names: string[]; // requested secret names (grant only; empty for unlock/enroll)
  reason: string;
  outcome: "approved" | "denied"; // the verdict actually sent (a declined Face ID sends nothing and is not recorded)
}

/**
 * Pure mapper: a decided request → its history record. No DOM, no IndexedDB — testable in
 * isolation. `approve` is the FINAL verdict (post Face-ID), so the recorded outcome matches
 * the signed verdict, not just the button pressed.
 */
export function toHistoryEntry(req: PendingGrant, approve: boolean, ts: number = Date.now()): HistoryEntry {
  const unlock = isUnlockRequest(req);
  const enrollAttest = isEnrollAttestRequest(req);
  const machine = req.target?.machine ?? req.target?.daemon_id ?? "(unknown machine)";
  if (isMintRequest(req)) {
    return {
      request_id: req.request_id,
      ts,
      kind: "mint",
      machine: req.descriptor.machine ?? machine,
      scope: `Service token "${req.descriptor.service_id ?? "?"}" · ${req.descriptor.wallets.join(", ")} · ${req.descriptor.ops.join(", ")}`,
      names: req.descriptor.names ?? [],
      reason: req.descriptor.reason,
      outcome: approve ? "approved" : "denied",
    };
  }
  const kind: HistoryEntry["kind"] = enrollAttest ? "enroll" : unlock ? "unlock" : "grant";
  const scope = enrollAttest
    ? `New device: ${req.descriptor.friendly_name ?? req.descriptor.gate_id ?? "(unnamed)"}`
    : unlock
      ? "Unlock the vault"
      : `${req.descriptor.wallets.join(", ")} · ${req.descriptor.ops.join(", ")}`;
  return {
    request_id: req.request_id,
    ts,
    kind,
    machine,
    scope,
    names: enrollAttest || unlock ? [] : req.descriptor.names ?? [],
    reason: enrollAttest ? "enroll new device" : unlock ? "unlock" : req.descriptor.reason,
    outcome: approve ? "approved" : "denied",
  };
}

/** Append a record to the local history log (newest-first, capped at HISTORY_MAX). Best-effort. */
export async function recordHistory(entry: HistoryEntry): Promise<void> {
  const list = (await dbGet<HistoryEntry[]>(HISTORY_KEY)) ?? [];
  list.unshift(entry);
  if (list.length > HISTORY_MAX) list.length = HISTORY_MAX;
  await dbSet(HISTORY_KEY, list);
}

/** Read the local history log (newest-first). Empty array when nothing has been decided yet. */
export async function getHistory(): Promise<HistoryEntry[]> {
  return (await dbGet<HistoryEntry[]>(HISTORY_KEY)) ?? [];
}
